import * as React from 'react';
import bindAllOfThis from 'src/utils/BindThisHelper';
import Routes from 'src/core/Routes';
import AudioPlayer from "src/components/common/AudioPlayer";
import { Button, Icon, Checkbox, Radio, Row, Col } from "antd";
import { Translate, LocalizeContextProps, withLocalize } from "react-localize-redux";
import AudioTranslations from "src/constants/AudioTranslations";
import { CheckboxValueType } from "antd/lib/checkbox/Group";
import StudentActivityUserQuestionResponseDTO from "src/models/activity/StudentActivityUserQuestionResponseDTO";
import StudentActivityQuestionDTO from "src/models/activity/StudentActivityQuestionDTO";
import { Game } from "src/constants/Games";
import StudentActivityQuestionResponseDTO from "src/models/activity/StudentActivityQuestionResponseDTO";

interface OrderedCategoryQuestion {
    order: number;
    categoryIndex: number;
    questionIndex: number;
    question: StudentActivityQuestionDTO;
}

interface ActivityQuestionDisplayProps extends LocalizeContextProps {
    // Doesn't change
    currentGame: Game;

    // Changes on every question
    currentQuestion: OrderedCategoryQuestion;
    studentResponses: StudentActivityUserQuestionResponseDTO[];
    canLeft: boolean;
    canRight: boolean;
    /** Used for audio loading only */
    isSurvey: boolean;
    isValid: boolean;
    isSubmitting: boolean;

    updateStudentResponses: (questionId: number, responseIds: number[]) => void;
    onPrev: () => void;
    onNext: () => void;
}

interface ActivityQuestionDisplayState {
    isCategory: boolean;
    /** Set when the category changes and needs to be read */
    isCategoryNew: boolean;
    hasQuestionFeedback: boolean;
    showQuestionFeedback: boolean;
}

class ActivityQuestionDisplay extends React.Component<ActivityQuestionDisplayProps, ActivityQuestionDisplayState> {
    constructor(props: ActivityQuestionDisplayProps) {
        super(props);
        bindAllOfThis(this, ActivityQuestionDisplay.prototype);

        this.state = {
            isCategory: false,
            isCategoryNew: false,
            hasQuestionFeedback: false,
            showQuestionFeedback: false,
        };
    }

    componentDidMount() {
        this.reloadComponent(this.props);
        document.addEventListener("keydown", this.handleKeyPress, false);
    }

    componentWillUnmount() {
        // Remove the event when we change pages
        document.removeEventListener("keydown", this.handleKeyPress, false);
    }

    componentWillReceiveProps(nextProps: ActivityQuestionDisplayProps) {
        if (nextProps.currentQuestion == null || nextProps.currentQuestion.question == null) {
            return;
        }
        // Used purely to check if the question has changed. We can also use its ID
        if (this.props.currentQuestion == null || this.props.currentQuestion.order !== nextProps.currentQuestion.order) {
            this.reloadComponent(nextProps);
        }
    }

    private reloadComponent(props: ActivityQuestionDisplayProps) {
        if (props.currentQuestion == null || props.currentQuestion.question == null) {
            return;
        }
        let currentCategoryId = this.props.currentQuestion != null && this.props.currentQuestion.question != null ? this.props.currentQuestion.question.category.id : 0;
        let newCategoryId = props.currentQuestion.question.category.id;

        let isCategory = newCategoryId > 0;
        let isCategoryNew = currentCategoryId !== newCategoryId;
        let feedback = props.currentQuestion.question.feedbackText;
        let hasQuestionFeedback = feedback != null && feedback.length > 1;

        this.setState({
            isCategory: isCategory,
            isCategoryNew: isCategoryNew,
            hasQuestionFeedback: hasQuestionFeedback,
            showQuestionFeedback: false,
        });
    }

    handleKeyPress(event): void {
        if (event.key === "ArrowRight") {
            return this.onNext();
        }
        else if (event.key === "ArrowLeft") {
            return this.onBack();
        }

        // Check if a 'number' was pressed
        if (!isNaN(event.key)) {
            let selectedNumber = Number(event.key);
            let question = this.props.currentQuestion.question;

            if (selectedNumber === 0 || selectedNumber > question.responses.length) {
                // 0 is not supported and the number pressed has to be in range
                return;
            }

            let selectedResponse = question.responses[selectedNumber - 1];

            if (!question.multipleSelection) {
                this.handleRadioChange(question.id, selectedResponse.id);
            } else {
                // Get current responses, flip the one we selected and push them out
                let studentResponse = this.props.studentResponses.find(x => x.activityQuestionResponseId === selectedResponse.id);
                let responseIds = this.props.studentResponses.map(x => x.activityQuestionResponseId);

                if (studentResponse == null) {
                    // Add it
                    responseIds.push(selectedResponse.id);
                } else {
                    // Remove it from array
                    responseIds = responseIds.filter(x => x !== selectedResponse.id);
                }
                // Submit array
                this.handleCheckboxChange(question.id, responseIds);
            }
        }
    }

    clearFocus() {
        let x = document.querySelector('div[tabIndex="1"]');
        (x as HTMLElement).focus();
    }

    onNext() {
        // Check if user has selected an answer
        if (this.props.studentResponses.length < 1) {
            return;
        }

        if (this.state.hasQuestionFeedback) {
            if (this.state.showQuestionFeedback) {
                if (this.props.canRight || !this.isSubmitDisabled()) {
                    this.clearFocus();
                    this.props.onNext();
                }
            } else {
                this.setState({ showQuestionFeedback: true });
            }
        } else {
            if (this.props.canRight || !this.isSubmitDisabled()) {
                this.clearFocus();
                this.props.onNext();
            }
        }
    }

    onBack() {
        this.props.onPrev();
    }

    private handleCheckboxChange(questionId: number, responseIds: CheckboxValueType[]) {
        this.props.updateStudentResponses(questionId, responseIds.map(Number));
    }

    private handleRadioChange(questionId: number, responseId: any) {
        this.props.updateStudentResponses(questionId, [Number(responseId)]);
    }

    isSubmitDisabled() {
        return !this.props.isValid || this.props.isSubmitting;
    }

    onAudioComplete() {
        if (this.state.isCategoryNew) {
            this.setState({ isCategoryNew: false });
        }
    }

    renderCategory() {
        let currentQuestion = this.props.currentQuestion;
        if (currentQuestion == null || currentQuestion.question == null || currentQuestion.question.category == null) {
            return null;
        }
        let titleText = currentQuestion.question.category.titleText;
        if (titleText == null || titleText.length === 0) {
            return null;
        }
        return <h3>{titleText}</h3>;
    }

    renderQuestionText() {
        let currentQuestion = this.props.currentQuestion;
        return <label className="activity-question-text">{currentQuestion.question.questionText}</label>;
    }

    renderResponseImage(response: StudentActivityQuestionResponseDTO) {
        // The url is coming back right now. In future, it shouldn't. For now, just check on the filename itself
        if (response.imageFile != null && response.imageFile.fileName != null && response.imageFile.fileName.length > 1) {
            return <img src={response.imageFile.azureFileUrl} />;
        }
    }

    selectResponse(e, currentQuestionId: number) {
        if (e.which === 32) // space
        {
            let selectedValue = e.target.querySelector('input').value;
            this.handleRadioChange(currentQuestionId, selectedValue);
        }
    }

    renderQuestionResponse() {
        let currentQuestion = this.props.currentQuestion.question;
        if (currentQuestion == null) {
            return null;
        }

        // let selectedResponses = this.props.studentResponses.filter(x => x.activityQuestionId === currentQuestion.id);
        let selections = this.props.studentResponses.map(x => x.activityQuestionResponseId);

        if (currentQuestion.multipleSelection) {
            let checkboxes = currentQuestion.responses
                .sort((first, second) => first.displayOrder - second.displayOrder)
                .map(response => {
                    return <Checkbox tabIndex={-1} key={`checkbox-${response.id}`} value={response.id} className="label-class">
                        {this.renderResponseImage(response)}
                        <span>{response.responseText}</span>
                    </Checkbox>;
                });
            return <Checkbox.Group key={`checkbox-group-${currentQuestion.id}`}
                className="question-response question-response-checkbox"
                value={selections}
                onChange={e => this.handleCheckboxChange(currentQuestion.id, e)}>
                <Row>{checkboxes.map(box => <Col tabIndex={1} onKeyPress={(e) => this.selectResponse(e, currentQuestion.id)} className="activity-response" span={24} key={"col" + box.key}>{box}</Col>)}</Row>
            </Checkbox.Group>;
        }   
        let radios = currentQuestion.responses.map(response => {
            return <Radio tabIndex={-1} key={`radio-${response.id}`} value={response.id} className="label-class">
                {this.renderResponseImage(response)}
                <span>{response.responseText}</span>
            </Radio>;
        });
        return <Radio.Group key={`radio-group-${currentQuestion.id}`}
            className="question-response question-response-radio"
            value={selections[0]}
            onChange={e => this.handleRadioChange(currentQuestion.id, e.target.value)}>
            <Row>{radios.map(radio => <Col tabIndex={1} onKeyPress={(e) => this.selectResponse(e, currentQuestion.id)} className="activity-response" span={24} key={"col" + radio.key}>{radio}</Col>)}</Row>
        </Radio.Group>;
    }

    renderNavigationButtons() {
        let canLeft = this.props.canLeft;
        let canRight = this.props.canRight;
        let hasFeedback = this.state.hasQuestionFeedback && !this.state.showQuestionFeedback;

        return <Button.Group>
            <Button tabIndex={1} onClick={this.onBack} disabled={!canLeft}>
                <Icon type="left" />
                <Translate id="common.previous" />
            </Button>
            {canRight || hasFeedback
                ? <Button tabIndex={1} onClick={this.onNext} disabled={this.props.studentResponses.length < 1}>
                    <Translate id="common.next" />
                    <Icon type="right" />
                </Button>
                : <Button className="finish-btn" tabIndex={1} onClick={this.onNext} type="primary" disabled={this.isSubmitDisabled()}>
                    <Icon type="check" />
                    <Translate id="common.finish" />
                </Button>
            }
        </Button.Group>;
    }

    renderFeedback() {
        if (!this.state.showQuestionFeedback) {
            return null;
        }
        let currentQuestion = this.props.currentQuestion;
        return <label>{currentQuestion.question.feedbackText}</label>;
    }

    render() {
        let currentQuestion = this.props.currentQuestion;
        if (currentQuestion == null) {
            return null;
        }

        let isCategory = this.state.isCategory;
        let isSurvey = this.props.isSurvey;
        let isCategoryNew = this.state.isCategoryNew;

        // Activities are now named: <Grade><Planet Code>.<Activity type>.
        let categoryKey = `${this.props.currentGame.grade.shortName}${this.props.currentGame.planet.name}.${isSurvey ? "Survey" : "Quiz"}`;
        if (isCategory) {
            categoryKey = `${categoryKey}.Category${currentQuestion.categoryIndex + 1}`;
        }

        let questionKey = `${categoryKey}.Question${currentQuestion.questionIndex + 1}`;
        let feedbackKey = `${questionKey}.Feedback`;
        let questionAudio = AudioTranslations.FindByKeyAndLanguage(questionKey, this.props.activeLanguage.code);
        let categoryAudio = AudioTranslations.FindByKeyAndLanguage(categoryKey, this.props.activeLanguage.code);
        let feedbackAudio = AudioTranslations.FindByKeyAndLanguage(feedbackKey, this.props.activeLanguage.code);

        return <div className="activity-form">
            <AudioPlayer
                audioURL={isCategoryNew ? categoryAudio.url : this.state.showQuestionFeedback ? feedbackAudio.url : questionAudio.url}
                isPlaying={true}
                onComplete={this.onAudioComplete} />

            {this.renderCategory()}
            {this.renderQuestionText()}
            {this.renderQuestionResponse()}
            {this.renderNavigationButtons()}
            {this.renderFeedback()}
        </div>;
    }
}

export default withLocalize(ActivityQuestionDisplay);



// WEBPACK FOOTER //
// ./src/components/student/ActivityQuestionDisplay.tsx